import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';

export interface ITableComponentConfiguration extends CM.DOMComponent.IConfiguration {
	isUsingManualRefresh?: boolean;
	hasMenu?: boolean;
	canUpdateRows?: boolean;
	rows?: Record<string, any>[];
};

export interface IRow {
	index: number;
	value: Record<string, any>;
};

export interface IDynamicCellConfiguration extends CM.Component.IConfiguration {
	typeValue?: string;
	typeConfiguration?: CM.Component.IConfiguration;
	template?: string; // templateId:instanceId
	templateConfigurations?: Record<string, CM.Component.IConfiguration>;
};

export class TableComponent extends CM.DOMComponent<
	HTMLDivElement,
	TableComponent,
	ITableComponentConfiguration
> {
	static override initialize() {
		window.addEventListener('resize', this.onResize.bind(this));
		document.addEventListener('click', this.onClick.bind(this));
		super.initialize();
	};

	private static onClick() {
		for (const instance of (<TableComponent[]>this.getInstances())) {
			if (instance.currentSelectedDOM) {
				instance.currentSelectedDOM.classList.remove('selected');
				instance.currentSelectedDOM = null;
			}
		}
	};

	private static async onResize() {
		for (const instance of (<TableComponent[]>this.getInstances())) {
			await instance.render();
		}
	};

	currentTable: HTMLDivElement;
	component_DOM: HTMLDivElement;
	columns: TableColumnComponent[] = [];
	rows: IRow[] = [];
	rowsIndex = 0;

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: ITableComponentConfiguration
	) {
		super(descriptor, configuration);
		this.currentTable = <HTMLDivElement>STD.DOM.factory();
		this.updateDOM = STD.misc.createDebouncerPromise({
			fn: this.updateDOM.bind(this),
			delay: 250
		});
		const struct: STD.DOM.IFactoryNodePartialDescriptor = {
			childs: [
				this.currentTable
			],
			events: {
				click: this.onTableClicked.bind(this)
			}
		};
		if (configuration.hasMenu) {
			(<any>struct.childs).unshift({
				classList: ['menu']
			});
		}
		this.component_DOM = <HTMLDivElement>STD.DOM.factory(struct);
		for (const rowDesc of (this.component_configuration.rows || [])) {
			this.rows.push({
				index: rowDesc['@id'] || this.rowsIndex++,
				value: rowDesc
			});
		}
	};

	private allowedChildsId: Set<string> = new Set;

	override async component_appendOuterChild(
		component: CM.Component<any, any>,
		slot: string | HTMLElement
	) {
		if (slot instanceof HTMLElement) {
			if (!slot.classList.contains('cell')) {
				this.component_throwUserError('Slot can only be a cell!', {
					childComponent: component,
					slot
				});
			}
		} else {
			if (component instanceof TableColumnComponent) {
				this.columns.push(component);
			} else if (component instanceof TableRowComponent) {
			} else if (this.allowedChildsId.has(component.id)) {
				// super.appendOuterChild(component, slot);
				return;
			} else {
				this.component_throwUserError('Component should be a table column or row', {
					childComponent: component
				});
			}
		}
		await super.component_appendOuterChild(component, slot);
		// if (!(slot instanceof HTMLElement)) {
		// 	await this.updateDOM();
		// }
	};

	// @TODO : This method is too large, it must be split!
	private async generateDOM() {
		const tableDOM = <HTMLDivElement>STD.DOM.factory({
			classList: ['table']
		});
		const oldTable = this.currentTable;
		this.currentTable = tableDOM;
		oldTable.replaceWith(this.currentTable);
		tableDOM.appendChild(STD.DOM.factory({
			classList: ['columns'],
			childs: this.columns.map(x => ({
				text: x.id,
				classList: ['cell']
			}))
		}));
		const toStart = [];
		for (const row of this.rows) {
			const rowDOM = STD.DOM.factory({
				attributes: {
					'data-row': row.index.toString()
				}
			});
			tableDOM.appendChild(rowDOM);
			for (const column of this.columns) {
				const cellDOM = STD.DOM.factory({
					classList: ['cell'],
					attributes: {
						'data-column': column.id
					}
				});
				rowDOM.appendChild(cellDOM);
				const configuration: IDynamicCellConfiguration = Object.assign(
					{},
					column.component_configuration.typeConfiguration
				);
				const childId = row.index + '-' + column.id;
				const path = this.component_getPath() + '.' + childId;
				for (const manifest of this.component_getManifestsList()) {
					const partialConf = manifest.deferedConfigurations[path];
					if (partialConf) {
						Object.assign(configuration, partialConf);
					}
				}
				const value = row.value[column.id];
				if (value !== undefined) {
					(<any>configuration).defaultValue = value; // no time to generalize, but remains that this code is weak, defaultValue is only for Input!
				}
				let child: CM.DOMComponent<any, any, any>;
				if (configuration.template) {
					const [templateId, instanceId] = <[string, string]>configuration.template.split(':');
					let lastError;
					let template;
					let manifestHostingTemplate;
					for (const manifest of this.component_getManifestsList()) {
						try {
							template = manifest.MP_template_find(templateId);
							manifestHostingTemplate = manifest;
						} catch (e: any) {
							if (!e.details || !e.details.isUserError || (e.details.errorCode !== 'NotFound')) {
								throw e;
							}
							if (lastError) {
								STD.errors.removeHandledError(lastError);
							}
							lastError = e;
						}
					}
					if (!template) {
						if (lastError) {
							throw lastError;
						} else {
							this.component_throwUserError('Referenced template not found!', {
								templateId,
								instanceId
							});
						}
					} else if (lastError) {
						STD.errors.removeHandledError(lastError);
					}
					if (!manifestHostingTemplate) {
						this.component_throwLibraryError('Unexpected error!', {});
					}
					const tmplCell = document.createElementNS(CM.Manifest.XMLNS.MP, 'mp:template-instance');
					tmplCell.id = instanceId;
					tmplCell.setAttributeNS(CM.Manifest.XMLNS.MP, 'mp:template', templateId);
					const templateInstance = manifestHostingTemplate.MP_template_specialize(
						tmplCell,
						template,
						childId
					);
					const top: CM.Component.TreeNode.RecursiveNode.TNode<any>[] = [];
					const parentPath = this.component_getPath();
					const topManifest = <CM.Manifest>this.component_getManifestsList().slice(-1)[0];
					const cellPath = parentPath + '.' + templateInstance.id;
					const childsConfigurations = (
						Object.entries(configuration.templateConfigurations || {}).
						reduce<Record<string, CM.Component.IConfiguration>>((a, [k, v]) => {
							a[cellPath + '.' + k] = v;
							return (a);
						}, {})
					);
					CM.Manifest.generateTreeNodes(
						[templateInstance],
						top,
						topManifest.configurations,
						parentPath,
						childsConfigurations
					);
					if (!top[0]) {
						// @TODO : Add a real error description after method split!
						this.component_throwUserError('', {});
					}
					const childNode = Object.assign({}, top[0], {
						tree: this.component_tree,
						slot: 'default'
					});
					this.allowedChildsId.add(childNode.id);
					const topChilds: CM.DOMComponent<any, any, any>[] = [];
					await CM.Manifest.ComponentManifest.instantiate(this, [childNode], topChilds);
					// await CM.Manifest.ComponentManifest.instantiate(this, [ childNode ], topChilds, 'outer', false);
					if (!topChilds[0]) {
						// @TODO : Add a real error description after method split!
						this.component_throwLibraryError('', {});
					}
					child = topChilds[0];
					cellDOM.appendChild(child.component_DOM);
				} else {
					const cellType = configuration.typeValue ? configuration.typeValue : column.typeValue.type;
					if (configuration.typeConfiguration) {
						Object.assign(configuration, configuration.typeConfiguration);
						delete configuration.typeConfiguration;
					}
					child = <CM.DOMComponent<any, any, any>>await this.component_instantiateOuterChild({
						type: cellType,
						id: childId,
						tree: this.component_tree,
						slot: null,
						isSlot: false,
						parentNode: cellDOM,
						configuration
					});
				}
				toStart.push(child);
			}
		}
		const startEvent = this.component_getEventWhichRepresentNoEvent();
		for (const c of toStart) {
			await c.component_start(startEvent.copy(c));
		}
		return (tableDOM);
	};

	private currentSelectedDOM: HTMLDivElement | null = null;

	private onTableClicked(ev: MouseEvent) {
		let t = <HTMLElement | null>ev.target;
		let row: HTMLDivElement | null = null;
		while (t && (t !== this.component_DOM)) {
			if (t.dataset.row && (t instanceof HTMLDivElement)) {
				row = t;
				break;
			}
			t = t.parentElement;
		}
		if (row && (this.currentSelectedDOM !== row)) {
			if (this.currentSelectedDOM) {
				this.currentSelectedDOM.classList.remove('selected');
			}
			row.classList.add('selected');
			this.currentSelectedDOM = row;
		}
		ev.preventDefault();
		ev.stopImmediatePropagation();
	};

	async updateDOM() {
		await this.generateDOM();
		await this.render();
	};

	override async component_onVisibilityUpdateAfterChilds(isVisible: boolean) {
		await super.component_onVisibilityUpdateAfterChilds(isVisible);
		await this.render();
	};

	async render() {
		const ppack = STD.misc.createPromisePack<void>();
		requestAnimationFrame(() => {
			try {
				const sizes: Record<number | string, number> = {};
				//compute sizes
				if (this.currentTable.children.length === 0) {
					return;
				}
				for (let ir = 0; ir < this.rows.length + 1; ir++) {
					const rowDOM = <HTMLDivElement>this.currentTable.children[ir];
					for (let ic = 0; ic < this.columns.length; ic++) {
						const cellDOM = <HTMLDivElement>rowDOM.children[ic];
						cellDOM.style.width = '';
						let currentW = cellDOM.offsetWidth;
						if (currentW < 10) {
							currentW = 0;
						}
						if (!sizes[ic] || (currentW > <number>sizes[ic])) {
							sizes[ic] = currentW;
						}
					}
				}
				const usedWidth = Object.values(sizes).reduce((a, c) => a + c, 0);
				const remaining = this.component_DOM.offsetWidth - usedWidth;
				const toDispatch = Math.floor(remaining / this.columns.length);
				for (const k of Object.keys(sizes)) {
					(<number>sizes[k]) += toDispatch;
				}
				//apply sizes
				for (let ir = 0; ir < this.rows.length + 1; ir++) {
					const rowDOM = <HTMLDivElement>this.currentTable.children[ir];
					for (let ic = 0; ic < this.columns.length; ic++) {
						const cellDOM = <HTMLDivElement>rowDOM.children[ic];
						cellDOM.style.width = sizes[ic] + 'px';
					}
				}
				ppack.resolve();
			} catch (error: any) {
				ppack.reject(STD.errors.bindAsBound('Unknown error!', {component: this}, error));
			}
		});
		await ppack.promise;
	};

	override async component_start(ev: STD.events.Event<any, any>) {
		await super.component_start(ev);
		await this.updateDOM();
	};
};
export namespace NS {
	export const Component = TableComponent;
	export type Component = TableComponent;
	export type Configuration = ITableComponentConfiguration;
}
;
import manifestXMLSource from './Table.xml';
import './Table.scss';
import {TableColumnComponent} from '../TableColumn/TableColumn';
import {TableRowComponent} from '../TableRow/TableRow';

CM.Manager.declare({
	id: 'table',
	type: TableComponent,
	manifest: manifestXMLSource
});
